<script>
  import Page from '@components/page.svelte'
  import Text from '@components/general/text.svelte'
  import Button from '@components/buttons/button.svelte'
  import Block from '@components/general/block'
  import Tag from '@components/general/tag.svelte'
  import Message from '@components/general/message.svelte'
  import SideList from '@components/videos/side-list.svelte'
  import ContributorBlock from '@components/contributors/info-block.svelte'
  import VideoForm from '@components/videos/form.svelte'
  import FeedbackFab from '@components/feedbacks/fab.svelte'
  import PremiumItemBlock from '@components/general/premium-item-block.svelte'
  import { useForm, router, page } from '@inertiajs/svelte'
  import { CirclePlay, Pencil, RefreshCw, ExternalLink, ChevronLeft } from 'lucide-svelte'
  import { formatDuration } from '@lib/utils'
  import ago from 's-ago'

  let { video, related_videos = [], feedback } = $props()

  let user = $page.props.user

  const sync = useForm()

  let formOpen = $state(false)
  
  let pageProps = $derived({
    pageTitle: video?.name || "Video",
    title: `Video: ${video.name}`,
    Icon: CirclePlay,
    adminLink: `/admin/videos/${video.id}`,
    links: [
      { text: "Videos", href: "/videos" },
    ],
    adminActions: [
      { text: "Edit", icon: Pencil, onSelect: () => formOpen = true },
      { text: "Sync with Vimeo", onSelect: syncVideo, icon: RefreshCw, disabled: $sync.processing },
      { text: "Manage on Vimeo", externalPath: `https://vimeo.com/manage/videos/${video.vimeo_id}`, icon: ExternalLink },
    ]
  })  

  function syncVideo() {
    $sync.post(`/admin/videos/${video.id}/sync`)
  }
</script>

<Page {...pageProps}>
  
  {#snippet tags()}
    {#each video.tags as tag (tag.id)}
      <Tag {tag} size="sm" onSelect={() => router.visit(`/videos?tag=${tag.id}`)} hint={`Show all videos tagged "${tag.name}"`} />
    {:else}
      <Tag tag={{ name: "Untagged", color: "gray" }} size="sm" />
    {/each}
  {/snippet}

  {#snippet buttons()}
    <Button variant="outline" path="/videos" text="Back" BeforeIcon={ChevronLeft} class="pl-1" />
  {/snippet}

  {#snippet main()}

    <!-- Video -->
    {#if video.can_watch}
      
      {#if video.vimeo_embed_url}
        <div class="w-full aspect-video rounded-lg overflow-hidden">
          <iframe title={video.name} src={video.vimeo_embed_url} 
            width="100%" height="100%" frameborder="0" allow="autoplay; fullscreen" allowfullscreen></iframe>
        </div>
      {:else}
        <Message type="error" title="Video not found">
          <p>The video embed URL is not available.</p>
        </Message>
      {/if}

    <!-- Locked video -->
    {:else}
      <PremiumItemBlock />
    {/if}

    <!-- Description -->
    <div class="pl-4 max-w-[80ch]">
      <Text variant="section_title" text="Summary" as="h5" class="mb-3" />
      <Text variant="long_text" text={video.description} />
    </div>

  {/snippet}

  {#snippet aside()}
    
    <div class="pt-6 flex flex-col gap-6">

      {#if video.contributor}
        <ContributorBlock contributor={video.contributor} heading="Presented by" />
      {/if}

      <SideList videos={related_videos} title="Related videos" />

      <Block.Items title="Video details">
        <Block.Item label="Uploaded" value={ago(new Date(video.vimeo_released_at || video.created_at))} />
        <Block.Item label="Duration" value={formatDuration(video.duration)} />
        {#if user.is_admin}
          <Block.Item label="Status">
            <Button variant={video.active ? "confirm" : "primary"} text={video.active ? "Active" : "Hidden"} size="xs" />
          </Block.Item>
        {/if}
        <Block.Item label="Access">
          <Button variant={video.free ? "confirm" : "primary"} text={video.free ? "Free" : "Premium"} size="xs" />
        </Block.Item>
      </Block.Items>

    </div>
    
  {/snippet}
</Page>

<VideoForm bind:open={formOpen} {video} />
<FeedbackFab {feedback} record={{ id: video.id, type: "Video", name: video.name }} />
